import { useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales/frFR';
import { Box } from '@mui/material';

const DENSITY = {
  compact: { rowHeight: 44, columnHeaderHeight: 44 },
  comfortable: { rowHeight: 52, columnHeaderHeight: 48 },
};

export default function DataTable({
  rows = [],
  columns = [],
  loading = false,
  pageSize = 25,
  height = 520,
  density = 'compact',
  ...rest
}) {
  const resolvedHeight = (typeof height === 'number' && height > 0) ? height : 520;
  const dens = DENSITY[density] || DENSITY.compact;

  const safeColumns = useMemo(
    () =>
      columns.map((col) => {
        const safeCol = { ...col };

        if (typeof col.renderCell === 'function') {
          safeCol.renderCell = (params) => {
            try {
              return col.renderCell(params);
            } catch {
              return '—';
            }
          };
        }

        if (typeof col.valueFormatter === 'function') {
          safeCol.valueFormatter = (value, row, column, apiRef) => {
            try {
              // Compatible v6 ({ value }) et v7 (value, row, …)
              if (value != null && typeof value === 'object' && 'value' in value && !Array.isArray(value)) {
                return col.valueFormatter(value);
              }
              return col.valueFormatter(value, row, column, apiRef);
            } catch {
              try {
                return col.valueFormatter({ value });
              } catch {
                return '—';
              }
            }
          };
        }

        if (typeof col.valueGetter === 'function') {
          safeCol.valueGetter = (value, row, column, apiRef) => {
            try {
              if (value != null && typeof value === 'object' && 'value' in value && 'row' in value) {
                return col.valueGetter(value);
              }
              return col.valueGetter(value, row, column, apiRef);
            } catch {
              try {
                return col.valueGetter({ value, row });
              } catch {
                return null;
              }
            }
          };
        }

        return safeCol;
      }),
    [columns],
  );

  return (
    <Box
      sx={{
        height: resolvedHeight,
        minHeight: 320,
        width: '100%',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <DataGrid
        rows={rows}
        columns={safeColumns}
        getRowId={(row) => row.id}
        loading={loading}
        rowHeight={dens.rowHeight}
        columnHeaderHeight={dens.columnHeaderHeight}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize } },
        }}
        disableRowSelectionOnClick
        localeText={{
          ...frFR.components.MuiDataGrid.defaultProps.localeText,
          footerRowSelected: (count) =>
            count !== 1
              ? `${count.toLocaleString()} lignes sélectionnées`
              : `${count.toLocaleString()} ligne sélectionnée`,
        }}
        slotProps={{
          pagination: {
            labelRowsPerPage: 'Lignes par page',
          },
        }}
        sx={{
          border: 0,
          bgcolor: 'background.paper',
          '& .MuiDataGrid-columnHeaders': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            fontSize: 12,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          },
          '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitle': {
            color: 'primary.main',
            fontWeight: 700,
          },
          '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
            color: 'primary.main',
          },
          '& .MuiDataGrid-filterIcon': {
            color: 'primary.main',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 0,
            fontSize: '0.875rem',
          },
          '& .MuiDataGrid-row:hover': {
            bgcolor: 'action.hover',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid',
            borderColor: 'divider',
            minHeight: 48,
          },
          '& .MuiTablePagination-root': {
            color: 'text.secondary',
          },
        }}
        {...rest}
      />
    </Box>
  );
}
